// Review queue (SPEC §4 Review.tsx) — open needs_review rows rendered as cards
// with answer controls GENERATED from each row's answer_schema jsonb:
//   - type "choice"  → one button per option; options[].requires (e.g.
//     {"drips_count":"int>=1"}) adds validated number inputs.
//   - duplicate-name → candidate-client picker built from the context jsonb,
//     falling back to a search box over v_client_list.
//   - anything else  → raw-JSON answer input (server has a generic resolver).
// Submit goes through the resolve_review RPC; a validation failure changes
// NOTHING server-side, so the card shows an error banner and stays open.

import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { supabase } from "../lib/supabase";
import { useQuery } from "../lib/useQuery";
import { money } from "../lib/format";
import { fmtDate, fmtDateTime } from "../lib/dates";
import type { AnswerOption, Id, NeedsReview } from "../lib/types";

/* ------------------------------------------------------------------ helpers */

interface Candidate {
  id: Id;
  label: string;
}

interface ClientPick {
  id: Id;
  full_name: string | null;
  email: string | null;
  phone_e164: string | null;
}

interface Draft {
  /** Selected option value (choice mode). */
  choice: string;
  /** Extra required fields per options[].requires, as raw input strings. */
  extras: Record<string, string>;
  /** Selected client (duplicate-name mode). */
  selected: Candidate | null;
  /** Client search box text (duplicate-name fallback). */
  search: string;
  /** Raw JSON answer (unknown-schema fallback). */
  raw: string;
}

const EMPTY_DRAFT: Draft = { choice: "", extras: {}, selected: null, search: "", raw: "" };

function isDuplicateName(row: NeedsReview): boolean {
  return /duplicate[_-]?name/i.test(row.trigger_type);
}

function isChoice(row: NeedsReview): boolean {
  return (
    !isDuplicateName(row) &&
    row.answer_schema?.type === "choice" &&
    (row.answer_schema.options?.length ?? 0) > 0
  );
}

/** Minimum for a requires-spec like "int>=1" (defaults to 1). */
function requiredMin(spec: string): number {
  const m = /(?:>=|≥)\s*(-?\d+)/.exec(spec);
  return m ? Number(m[1]) : 1;
}

function firstString(rec: Record<string, unknown>, keys: string[]): string | null {
  for (const k of keys) {
    const v = rec[k];
    if (typeof v === "string" && v.trim()) return v.trim();
  }
  return null;
}

function candidateLabel(rec: Record<string, unknown>, id: Id): string {
  const name =
    firstString(rec, ["full_name", "name", "norm_name"]) ??
    [firstString(rec, ["first_name"]), firstString(rec, ["last_name"])]
      .filter(Boolean)
      .join(" ")
      .trim();
  const detail = firstString(rec, ["email", "phone_e164", "phone"]);
  if (name && detail) return `${name} · ${detail}`;
  if (name) return name;
  if (detail) return detail;
  return `Client ${String(id)}`;
}

/**
 * Pull candidate clients out of the context jsonb without assuming its exact
 * shape: any array of objects carrying id/client_id counts; arrays of bare ids
 * count when their key looks candidate/client-ish.
 */
function extractCandidates(context: Record<string, unknown> | null): Candidate[] {
  if (!context) return [];
  const out: Candidate[] = [];
  const seen = new Set<string>();
  const push = (id: Id, label: string) => {
    const key = String(id);
    if (seen.has(key)) return;
    seen.add(key);
    out.push({ id, label });
  };
  for (const [key, value] of Object.entries(context)) {
    if (!Array.isArray(value)) continue;
    for (const item of value) {
      if (item && typeof item === "object" && !Array.isArray(item)) {
        const rec = item as Record<string, unknown>;
        const rawId = rec["client_id"] ?? rec["id"];
        if (typeof rawId === "string" || typeof rawId === "number") {
          push(rawId, candidateLabel(rec, rawId));
        }
      } else if (
        (typeof item === "string" || typeof item === "number") &&
        /candidate|client|match/i.test(key)
      ) {
        push(item, `Client ${String(item)}`);
      }
    }
  }
  return out;
}

const CONTEXT_MONEY_KEYS = new Set(["sale_total", "amount", "total", "face_total", "ltv"]);
const CONTEXT_DATE_KEYS = new Set(["sale_date", "date", "first_visit", "last_visit"]);
const CONTEXT_SKIP_KEYS = new Set(["client_id", "sale_id"]);

function prettyKey(key: string): string {
  return key.replace(/_/g, " ");
}

function contextValue(key: string, value: unknown): string {
  if (value == null) return "—";
  if (CONTEXT_MONEY_KEYS.has(key) && (typeof value === "number" || typeof value === "string")) {
    const rendered = money(value);
    if (rendered !== "—") return rendered;
  }
  if (CONTEXT_DATE_KEYS.has(key) && (typeof value === "string" || typeof value === "number")) {
    const rendered = fmtDate(value);
    if (rendered !== "—") return rendered;
  }
  if (typeof value === "object") return JSON.stringify(value);
  return String(value);
}

/** Sort newest-first by created_at when the column exists; stable otherwise. */
function sortByRecency(rows: NeedsReview[], field: "created_at" | "resolved_at"): NeedsReview[] {
  return [...rows].sort((a, b) => {
    const av = a[field] ?? a.created_at ?? "";
    const bv = b[field] ?? b.created_at ?? "";
    return String(bv).localeCompare(String(av));
  });
}

/* ------------------------------------------------------------------ context */

function ContextView({ row }: { row: NeedsReview }) {
  const context = row.context;
  const clientId = row.client_id ?? (context?.["client_id"] as Id | undefined) ?? null;
  const entries = context
    ? Object.entries(context).filter(([k, v]) => !CONTEXT_SKIP_KEYS.has(k) && !Array.isArray(v))
    : [];
  if (!context && clientId == null) return null;
  return (
    <div className="review-context">
      <div className="kv">
        {clientId != null && (
          <>
            <span className="kv-label">client</span>
            <span>
              <Link to={`/clients/${String(clientId)}`}>Open client record</Link>
            </span>
          </>
        )}
        {entries.map(([k, v]) => (
          <span key={`${k}-pair`} style={{ display: "contents" }}>
            <span className="kv-label">{prettyKey(k)}</span>
            <span className={typeof v === "object" && v != null ? "mono" : undefined}>
              {contextValue(k, v)}
            </span>
          </span>
        ))}
      </div>
    </div>
  );
}

/* --------------------------------------------------------------------- page */

export default function Review() {
  const [tab, setTab] = useState<"open" | "resolved">("open");
  const [drafts, setDrafts] = useState<Record<string, Draft>>({});
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [busyId, setBusyId] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const openQ = useQuery<NeedsReview[]>(async () => {
    const { data, error } = await supabase.from("needs_review").select("*").eq("status", "open");
    if (error) throw new Error(error.message);
    return sortByRecency((data ?? []) as NeedsReview[], "created_at");
  }, []);

  const resolvedQ = useQuery<NeedsReview[]>(async () => {
    if (tab !== "resolved") return [];
    const { data, error } = await supabase
      .from("needs_review")
      .select("*")
      .eq("status", "resolved")
      .order("resolved_at", { ascending: false, nullsFirst: false })
      .order("id", { ascending: false })
      .limit(200);
    if (error) throw new Error(error.message);
    return sortByRecency((data ?? []) as NeedsReview[], "resolved_at");
  }, [tab]);

  const openRows = openQ.data ?? [];
  const anyDuplicate = openRows.some(isDuplicateName);

  // Client list for the duplicate-name fallback search — fetched lazily, only
  // when at least one open card needs it.
  const clientsQ = useQuery<ClientPick[]>(async () => {
    if (!anyDuplicate) return [];
    const { data, error } = await supabase
      .from("v_client_list")
      .select("id,full_name,email,phone_e164");
    if (error) throw new Error(error.message);
    return (data ?? []) as ClientPick[];
  }, [anyDuplicate]);

  const draftFor = (id: string): Draft => drafts[id] ?? EMPTY_DRAFT;

  function patchDraft(id: string, patch: Partial<Draft>) {
    setDrafts((d) => ({ ...d, [id]: { ...(d[id] ?? EMPTY_DRAFT), ...patch } }));
    setErrors((e) => ({ ...e, [id]: "" }));
  }

  /** Build + validate the answer payload for a card; throws with a message. */
  function buildAnswer(row: NeedsReview, draft: Draft): Record<string, unknown> {
    if (isDuplicateName(row)) {
      if (!draft.selected) throw new Error("Pick the client this sale belongs to first.");
      return { client_id: draft.selected.id };
    }
    if (isChoice(row)) {
      const options = row.answer_schema?.options ?? [];
      const opt = options.find((o) => o.value === draft.choice);
      if (!opt) throw new Error("Pick one of the options first.");
      const answer: Record<string, unknown> = { choice: opt.value };
      for (const [key, spec] of Object.entries(opt.requires ?? {})) {
        const rawValue = (draft.extras[key] ?? "").trim();
        const n = Number(rawValue);
        const min = requiredMin(spec);
        if (!rawValue || !Number.isInteger(n) || n < min) {
          throw new Error(`"${prettyKey(key)}" must be a whole number ≥ ${min}.`);
        }
        answer[key] = n;
      }
      return answer;
    }
    // Unknown schema → raw JSON fallback (server-side generic resolver).
    if (!draft.raw.trim()) throw new Error("Enter the answer as JSON first.");
    let parsed: unknown;
    try {
      parsed = JSON.parse(draft.raw);
    } catch {
      throw new Error("Answer must be valid JSON.");
    }
    if (!parsed || typeof parsed !== "object" || Array.isArray(parsed)) {
      throw new Error("Answer must be a JSON object.");
    }
    return parsed as Record<string, unknown>;
  }

  async function submit(row: NeedsReview) {
    const key = String(row.id);
    setNotice(null);
    let answer: Record<string, unknown>;
    try {
      answer = buildAnswer(row, draftFor(key));
    } catch (err) {
      setErrors((e) => ({ ...e, [key]: err instanceof Error ? err.message : String(err) }));
      return;
    }
    setBusyId(key);
    try {
      const { error } = await supabase.rpc("resolve_review", {
        p_review_id: row.id,
        p_answer: answer,
      });
      if (error) throw new Error(error.message);
      setDrafts((d) => {
        const next = { ...d };
        delete next[key];
        return next;
      });
      setNotice("Review resolved.");
      openQ.reload();
      if (tab === "resolved") resolvedQ.reload();
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      setErrors((e) => ({ ...e, [key]: `Nothing was changed — ${msg}` }));
    } finally {
      setBusyId(null);
    }
  }

  /* ------------------------------------------------------- answer controls */

  function renderChoiceControls(row: NeedsReview, key: string, draft: Draft) {
    const options: AnswerOption[] = row.answer_schema?.options ?? [];
    const active = options.find((o) => o.value === draft.choice);
    return (
      <>
        <div className="review-actions">
          {options.map((opt) => (
            <button
              key={opt.value}
              type="button"
              className={"btn" + (draft.choice === opt.value ? " btn-primary" : " btn-ghost")}
              onClick={() => patchDraft(key, { choice: opt.value, extras: {} })}
              disabled={busyId === key}
            >
              {opt.label}
            </button>
          ))}
        </div>
        {active &&
          Object.entries(active.requires ?? {}).map(([reqKey, spec]) => (
            <div className="field mt-1" key={reqKey} style={{ maxWidth: 220 }}>
              <label htmlFor={`req-${key}-${reqKey}`}>{prettyKey(reqKey)}</label>
              <input
                id={`req-${key}-${reqKey}`}
                type="number"
                min={requiredMin(spec)}
                step={1}
                value={draft.extras[reqKey] ?? ""}
                onChange={(e) =>
                  patchDraft(key, { extras: { ...draft.extras, [reqKey]: e.target.value } })
                }
                disabled={busyId === key}
              />
              <span className="field-hint">{spec}</span>
            </div>
          ))}
      </>
    );
  }

  function renderDuplicateControls(row: NeedsReview, key: string, draft: Draft) {
    const candidates = extractCandidates(row.context);
    const clients = clientsQ.data ?? [];
    const q = draft.search.trim().toLowerCase();
    const matches = q
      ? clients
          .filter(
            (c) =>
              (c.full_name ?? "").toLowerCase().includes(q) ||
              (c.email ?? "").toLowerCase().includes(q) ||
              (c.phone_e164 ?? "").includes(q),
          )
          .slice(0, 8)
      : [];
    return (
      <>
        {candidates.length > 0 && (
          <div className="mb-1">
            <div className="field-hint mb-1">Candidate clients from the flagged context:</div>
            <div className="review-actions">
              {candidates.map((c) => (
                <button
                  key={String(c.id)}
                  type="button"
                  className={
                    "btn" +
                    (draft.selected && String(draft.selected.id) === String(c.id)
                      ? " btn-primary"
                      : " btn-ghost")
                  }
                  onClick={() => patchDraft(key, { selected: c })}
                  disabled={busyId === key}
                >
                  {c.label}
                </button>
              ))}
            </div>
          </div>
        )}
        <div className="field" style={{ maxWidth: 360 }}>
          <label htmlFor={`search-${key}`}>
            {candidates.length > 0 ? "Or search all clients" : "Search clients"}
          </label>
          <input
            id={`search-${key}`}
            type="search"
            placeholder="name, email, or phone…"
            value={draft.search}
            onChange={(e) => patchDraft(key, { search: e.target.value })}
            disabled={busyId === key}
          />
          {clientsQ.loading && q && <span className="field-hint">Loading client list…</span>}
          {clientsQ.error && <span className="field-hint red">{clientsQ.error}</span>}
        </div>
        {matches.length > 0 && (
          <div className="review-actions mb-1">
            {matches.map((c) => {
              const label =
                (c.full_name ?? `Client ${String(c.id)}`) +
                (c.email ? ` · ${c.email}` : c.phone_e164 ? ` · ${c.phone_e164}` : "");
              return (
                <button
                  key={String(c.id)}
                  type="button"
                  className={
                    "btn btn-sm" +
                    (draft.selected && String(draft.selected.id) === String(c.id)
                      ? " btn-primary"
                      : " btn-ghost")
                  }
                  onClick={() => patchDraft(key, { selected: { id: c.id, label } })}
                  disabled={busyId === key}
                >
                  {label}
                </button>
              );
            })}
          </div>
        )}
        {draft.selected && (
          <div className="field-hint">
            Assigning to: <span className="accent bold">{draft.selected.label}</span>
          </div>
        )}
      </>
    );
  }

  function renderRawControls(_row: NeedsReview, key: string, draft: Draft) {
    return (
      <div className="field">
        <label htmlFor={`raw-${key}`}>Answer (JSON)</label>
        <textarea
          id={`raw-${key}`}
          className="mono"
          placeholder='{"choice": "…"}'
          value={draft.raw}
          onChange={(e) => patchDraft(key, { raw: e.target.value })}
          disabled={busyId === key}
        />
        <span className="field-hint">
          This card has no recognized answer schema — the answer is stored as-is.
        </span>
      </div>
    );
  }

  function renderCard(row: NeedsReview) {
    const key = String(row.id);
    const draft = draftFor(key);
    return (
      <div className="review-card" key={key}>
        <div className="flex flex-wrap mb-1">
          <span className="badge badge-amber">{prettyKey(row.trigger_type)}</span>
          {row.sale_id != null && <span className="muted small">sale #{String(row.sale_id)}</span>}
        </div>
        <div className="review-question">{row.question}</div>
        <ContextView row={row} />
        {errors[key] && <div className="error-banner">{errors[key]}</div>}
        {isDuplicateName(row)
          ? renderDuplicateControls(row, key, draft)
          : isChoice(row)
            ? renderChoiceControls(row, key, draft)
            : renderRawControls(row, key, draft)}
        <div className="review-actions mt-1">
          <button
            type="button"
            className="btn btn-primary"
            onClick={() => void submit(row)}
            disabled={busyId === key}
          >
            {busyId === key ? "Resolving…" : "Resolve"}
          </button>
        </div>
        {row.created_at && <div className="review-meta">Flagged {fmtDateTime(row.created_at)}</div>}
      </div>
    );
  }

  function renderResolvedCard(row: NeedsReview) {
    return (
      <div className="review-card resolved" key={String(row.id)}>
        <div className="flex flex-wrap mb-1">
          <span className="badge badge-green">resolved</span>
          <span className="badge badge-muted">{prettyKey(row.trigger_type)}</span>
          {row.client_id != null && (
            <Link className="small" to={`/clients/${String(row.client_id)}`}>
              client record
            </Link>
          )}
        </div>
        <div className="review-question">{row.question}</div>
        {row.answer != null && (
          <div className="review-context">
            <span className="kv-label">answer </span>
            <span className="mono">{JSON.stringify(row.answer)}</span>
          </div>
        )}
        <div className="review-meta">
          {row.resolved_at
            ? `Resolved ${fmtDateTime(row.resolved_at)}`
            : row.created_at
              ? `Flagged ${fmtDateTime(row.created_at)}`
              : ""}
        </div>
      </div>
    );
  }

  /* ---------------------------------------------------------------- render */

  const openCount = openRows.length;
  const resolvedRows = useMemo(() => resolvedQ.data ?? [], [resolvedQ.data]);

  return (
    <div className="page">
      <div className="page-header">
        <div>
          <h1 className="page-title">Review</h1>
          <div className="page-sub">
            Flagged sales that need a human decision before the numbers can be trusted.
          </div>
        </div>
        <div className="page-actions">
          <button
            type="button"
            className={"btn btn-sm" + (tab === "open" ? " btn-primary" : " btn-ghost")}
            onClick={() => setTab("open")}
          >
            Open{openQ.data ? ` (${openCount})` : ""}
          </button>
          <button
            type="button"
            className={"btn btn-sm" + (tab === "resolved" ? " btn-primary" : " btn-ghost")}
            onClick={() => setTab("resolved")}
          >
            Resolved
          </button>
          <button
            type="button"
            className="btn btn-ghost btn-sm"
            onClick={() => (tab === "open" ? openQ.reload() : resolvedQ.reload())}
          >
            Refresh
          </button>
        </div>
      </div>

      {notice && <div className="success-banner">{notice}</div>}

      {tab === "open" && (
        <>
          {openQ.error && <div className="error-banner">{openQ.error}</div>}
          {openQ.loading && !openQ.data && (
            <div className="loading">
              <span className="spinner" /> Loading review queue…
            </div>
          )}
          {openQ.data && openCount === 0 && (
            <div className="empty-state">Nothing needs review. The queue is clear.</div>
          )}
          {openRows.map(renderCard)}
        </>
      )}

      {tab === "resolved" && (
        <>
          {resolvedQ.error && <div className="error-banner">{resolvedQ.error}</div>}
          {resolvedQ.loading && !resolvedQ.data && (
            <div className="loading">
              <span className="spinner" /> Loading history…
            </div>
          )}
          {resolvedQ.data && resolvedRows.length === 0 && (
            <div className="empty-state">No resolved reviews yet.</div>
          )}
          {resolvedRows.map(renderResolvedCard)}
        </>
      )}
    </div>
  );
}
